import { registerChildWindow } from 'components/CallScribe/popupChannel';
import {
  type ISoftphoneLiveCall,
  hasReportedIn,
  liveCallIn,
  watchLiveCalls,
} from 'components/CallScribe/softphoneLiveCall';
import {
  type IPopupCallContext,
  newLaunchId,
  writePopupCallContext,
} from 'components/CallScribe/popupCallContext';
import {
  newPopupTicket,
  registerPopupTicket,
  withPopupTicket,
} from 'components/CallScribe/popupTicket';
import { callPopupFeatures } from 'components/CallScribe/popupWindowFeatures';

/**
 * Helpers for opening the CallScribe softphone in a top-level popup window.
 *
 * When the Pod is embedded in a PSA (Halo, ConnectWise, Autotask) iframe, the parent
 * page rarely sets `allow="microphone"` on its iframe element. In that case
 * `navigator.mediaDevices.getUserMedia` rejects with `NotAllowedError` *without ever
 * prompting* the user — there is no JS workaround at the iframed page level.
 *
 * The escape hatch is to open the same Pod URL in a real browser window via
 * `window.open(...)`. The popup is NOT iframed, so the mic prompt fires normally and
 * Twilio Voice can acquire audio.
 */

const POPUP_NAME_PREFIX = 'mspp-callscribe-';
const MAX_TRACKED_POPUPS = 10;
export const LAUNCH_SETTLE_MS = 3000;

/** Returns true when the current document is rendered inside another window/frame. */
export const isEmbeddedInIframe = (): boolean => {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin parent throws on `window.top` access — that itself proves we're iframed.
    return true;
  }
};

const focusWindow = (opened: Window): void => {
  try {
    opened.focus();
  } catch {
    return undefined;
  }
};

let softphones: Window[] = [];
let launched = false;
let lastLaunch: { popup: Window; at: number } | null = null;

export const hasLaunchedSoftphone = (): boolean => launched;

const liveSoftphone = (): { popup: Window; call: ISoftphoneLiveCall } | null => {
  for (const popup of [...softphones].reverse()) {
    const call = liveCallIn(popup);
    if (call) return { popup, call };
  }
  return null;
};

export const softphoneLiveCall = (): ISoftphoneLiveCall | null => liveSoftphone()?.call ?? null;

export const focusSoftphone = (): void => {
  const live = liveSoftphone();
  if (live) focusWindow(live.popup);
};

const isOpen = (popup: Window): boolean => {
  try {
    return !popup.closed;
  } catch {
    return false;
  }
};

const trackSoftphone = (popup: Window): void => {
  softphones = [...softphones.filter(w => w !== popup && isOpen(w)), popup].slice(-MAX_TRACKED_POPUPS);
};

const knownOpen = (popup: Window): boolean => {
  try {
    const closed: unknown = popup.closed;
    return closed === false;
  } catch {
    return false;
  }
};

const launchInFlight = (): Window | null => {
  if (!lastLaunch || Date.now() - lastLaunch.at >= LAUNCH_SETTLE_MS) return null;
  const { popup } = lastLaunch;
  return knownOpen(popup) && !hasReportedIn(popup) ? popup : null;
};

const activationWasSpent = (): boolean => {
  try {
    return navigator.userActivation ? !navigator.userActivation.isActive : false;
  } catch {
    return false;
  }
};

export const openSoftphonePopup = (context: IPopupCallContext): Window | null => {
  watchLiveCalls();
  const inFlight = launchInFlight();
  if (inFlight) {
    focusWindow(inFlight);
    return inFlight;
  }
  launched = true;
  const url = new URL(window.location.href);
  url.searchParams.set('softphone', 'popup');
  const launchId = newLaunchId();
  writePopupCallContext(url, context, launchId);
  const ticket = newPopupTicket();
  const target = withPopupTicket(url.toString(), ticket);

  const popup = window.open(target, `${POPUP_NAME_PREFIX}${launchId}`, callPopupFeatures());
  lastLaunch = popup ? { popup, at: Date.now() } : null;
  if (popup) trackSoftphone(popup);
  registerChildWindow(popup);
  if (popup) {
    focusWindow(popup);
  } else if (!activationWasSpent()) {
    window.open(target, '_blank', 'noopener');
  }
  void registerPopupTicket(ticket);
  return popup;
};

/** Returns true when the current document is the popup softphone view. */
export const isSoftphonePopupView = (): boolean => {
  try {
    return new URLSearchParams(window.location.search).get('softphone') === 'popup';
  } catch {
    return false;
  }
};

/** Returns the dial target the parent passed to the popup, if any. */
export const getPopupDialTarget = (): string | undefined => {
  try {
    return new URLSearchParams(window.location.search).get('to') ?? undefined;
  } catch {
    return undefined;
  }
};
